/**
 * /simulador — landing shows histórico + conquistas. New simulação opens
 * via a "Nova simulação" button → modal Dialog (matches Decodificar pattern).
 *
 * Mounts call `resumeIfActive` so refreshing the page returns the user
 * straight to the in-progress conversation.
 *
 * Spec: docs/architecture/simulador-spec.md §5.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Spinner } from "@phosphor-icons/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePageTitle } from "@/hooks/usePageTitle";
import { SetupCard } from "@/features/simulador/components/SetupCard";
import { SimuladorChat } from "@/features/simulador/components/SimuladorChat";
import { SimuladorChatSkeleton } from "@/features/simulador/components/SimuladorChatSkeleton";
import { EndedOverlay } from "@/features/simulador/components/EndedOverlay";
import { AchievementsGrid } from "@/features/simulador/components/AchievementsGrid";
import { HistoryList } from "@/features/simulador/components/HistoryList";
import { useSimulationSession } from "@/features/simulador/hooks/useSimulationSession";
import { useUserProcedures } from "@/features/simulador/hooks/useUserProcedures";
import { useSimulatorHint } from "@/features/simulador/hooks/useSimulatorHint";
import { useSimulatorAchievements } from "@/features/simulador/hooks/useSimulatorAchievements";
import { useSimulatorSessions } from "@/features/simulador/hooks/useSimulatorSessions";
import type {
  CloseVerdict,
  SimulatorStatus,
  SimulatorMessage,
} from "@/features/simulador/types";
import { HelpButton } from "@/components/ui/help-button";

export default function Simulador() {
  usePageTitle("Simulador");
  const sim = useSimulationSession();
  const proceduresQuery = useUserProcedures();
  const hint = useSimulatorHint();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const achievementsQ = useSimulatorAchievements();
  const sessionsQ = useSimulatorSessions();
  const [openNewSim, setOpenNewSim] = useState(false);

  // Mount-time resume: if the user has an active/paused session, jump
  // straight back into chat with full transcript.
  useEffect(() => {
    void sim.resumeIfActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the new-simulation modal as soon as a session starts spinning up.
  useEffect(() => {
    if (sim.view.kind === "starting" || sim.view.kind === "chat") {
      setOpenNewSim(false);
    }
  }, [sim.view.kind]);

  useEffect(() => {
    if (sim.view.kind !== "setup") return;
    if (searchParams.get("new") !== "1") return;
    setOpenNewSim(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("new");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, sim.view.kind]);

  // Detect terminal status by inspecting the latest patient message's
  // classifier_meta annotation we stash in `useSimulationSession.reply`.
  const terminal = useMemo<{
    status: Extract<SimulatorStatus, "close_won" | "close_lost">;
    verdict?: CloseVerdict;
  } | null>(() => {
    if (sim.view.kind !== "chat") return null;
    const last = [...sim.view.messages].reverse().find((m) => m.role === "patient");
    if (!last?.classifier_meta) return null;
    const meta = last.classifier_meta as { status?: string; verdict?: CloseVerdict };
    if (meta.status === "close_won" || meta.status === "close_lost") {
      return { status: meta.status, verdict: meta.verdict };
    }
    return null;
  }, [sim.view]);

  // Auto-navigate to /simulador/relatorio/:id once the session is terminal.
  const navigatedRef = useRef<string | null>(null);
  useEffect(() => {
    if (sim.view.kind === "ended") {
      const id = sim.view.sessionId;
      if (id && navigatedRef.current !== id) {
        navigatedRef.current = id;
        navigate(`/simulador/relatorio/${id}`);
      }
    }
  }, [sim.view, navigate]);

  return (
    <AppLayout>
      {sim.isResuming ? (
        <SimuladorChatSkeleton />
      ) : sim.view.kind === "setup" ? (
        <div className="space-y-6 animate-fade-in">
          {/* Header + CTA */}
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-foreground">
                  Simulador de <span className="accent-text">Atendimento</span>
                </h1>
                <HelpButton
                  title="Simulador de Atendimento"
                  description="Treine consultas de venda com pacientes/leads simulados."
                >
                  <p>
                    Pratica conversas com leads de cada Código (C1–C9) antes de
                    enfrentá-los na vida real. A IA atua como o lead, com dor,
                    objeções e ritmo coerentes com a personalidade escolhida.
                  </p>
                  <p>
                    <strong>Como usar:</strong> selecione o tipo de lead, abra
                    a sessão e atenda como faria numa call. Ao fim, gere o
                    relatório — pontos fortes, falhas, etapa do método mais
                    fraca.
                  </p>
                  <p className="text-muted-foreground">
                    Etapa do método: ensaio. Use antes de calls importantes ou
                    depois de uma consulta perdida pra refazer com hindsight.
                  </p>
                </HelpButton>
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                Treine atendimentos com pacientes simulados de cada personalidade.
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => setOpenNewSim(true)}
              className="gap-2 glow-primary-sm"
              data-testid="open-new-simulation"
            >
              <Plus weight="bold" className="w-4 h-4" />
              Nova simulação
            </Button>
          </header>

          {/* Achievements (also shown in setup so user sees progress at a glance) */}
          <AchievementsGrid
            achievements={achievementsQ.data?.achievements ?? []}
            isLoading={achievementsQ.isPending}
          />

          {/* History */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">
              Histórico de simulações
            </h2>
            <HistoryList
              sessions={sessionsQ.data ?? []}
              isLoading={sessionsQ.isPending}
              onResume={(sessionId) => sim.resume(sessionId)}
            />
          </section>

          {/* New simulation modal — Decodificar Dialog pattern */}
          <Dialog open={openNewSim} onOpenChange={setOpenNewSim}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nova simulação</DialogTitle>
              </DialogHeader>
              <SetupCard
                procedures={proceduresQuery.data ?? []}
                isLoadingProcedures={proceduresQuery.isPending}
                isStarting={sim.isStarting}
                onStart={(en, diff, procId) => void sim.start(en, diff, procId)}
              />
            </DialogContent>
          </Dialog>
        </div>
      ) : sim.view.kind === "starting" ? (
        <SimuladorChatSkeleton />
      ) : sim.view.kind === "chat" ? (
        <div className="relative flex min-h-[calc(100dvh-8.5rem)] flex-col">
          <SimuladorChat
            persona={sim.view.persona}
            procedure={sim.view.procedure}
            difficulty={sim.view.difficulty}
            messages={sim.view.messages}
            isReplying={sim.isReplying}
            onBack={() => sim.reset()}
            onSend={(c) => void sim.reply(c)}
            onEnd={() => void sim.end("user_quit")}
            onRequestHint={async () => {
              if (sim.view.kind !== "chat") return;
              const sessionId = sim.view.sessionId;
              const res = await hint.ask(sessionId);
              if (!res) return;
              const hintMsg: SimulatorMessage = {
                seq: res.seq,
                role: "hint",
                content: res.hint,
                created_at: new Date().toISOString(),
              };
              sim.appendLocalMessage(hintMsg);
            }}
            hintLoading={hint.isLoading}
            hintPenaltyTotal={hint.lastHint?.hint_penalty_total ?? 0}
          />
          {terminal && sim.view.kind === "chat" ? (
            <EndedOverlay
              status={terminal.status}
              verdict={terminal.verdict}
              onViewReport={() => {
                if (sim.view.kind === "chat") {
                  const id = sim.view.sessionId;
                  void sim.end("user_quit");
                  navigate(`/simulador/relatorio/${id}`);
                }
              }}
              onBackToSetup={() => sim.reset()}
            />
          ) : null}
        </div>
      ) : sim.view.kind === "ended" ? (
        <CenteredSpinner label="Abrindo relatório…" />
      ) : (
        <CenteredSpinner label="Abrindo relatório…" />
      )}
    </AppLayout>
  );
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
      <Spinner
        weight="bold"
        className="w-8 h-8 animate-spin text-primary"
        aria-hidden="true"
      />
      <p className="text-sm">{label}</p>
    </div>
  );
}
